import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { listCustomDashboardRevisions } from '@/lib/api/custom-dashboard';

export interface RevisionHistoryDialogProps {
  accountId: string;
  authHeaders: Record<string, string>;
  tabId: string;
  tabName: string;
  activeRevision: number | null;
  /** ready な revision のプレビュー要求。呼び出し元がプレビューダイアログを開く。 */
  onPreview: (revision: number) => void;
  onClose: () => void;
}

const statusBadgeVariant = (status: string) => {
  if (status === 'failed') return 'destructive' as const;
  return status === 'ready' ? ('secondary' as const) : ('outline' as const);
};

const formatDateTime = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : format(date, 'yyyy/MM/dd HH:mm');
};

const formatSize = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${(bytes / 1024).toFixed(1)} KB`;

/** タブのアップロード履歴（revision 一覧）を表示するダイアログ。 */
export function RevisionHistoryDialog({
  accountId,
  authHeaders,
  tabId,
  tabName,
  activeRevision,
  onPreview,
  onClose,
}: RevisionHistoryDialogProps) {
  const query = useQuery({
    queryKey: ['custom-dashboard-revisions', accountId, tabId],
    queryFn: ({ signal }) => listCustomDashboardRevisions(accountId, tabId, authHeaders, signal),
  });
  // BE も revision DESC で返す（LIST_REVISIONS_SQL）が、表示仕様としての降順をここで保証する。
  const revisions = [...(query.data?.revisions ?? [])].sort((a, b) => b.revision - a.revision);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{tabName} のアップロード履歴</DialogTitle>
          <DialogDescription>
            ready の revision はプレビューで描画を確認してから公開・ロールバックできます
          </DialogDescription>
          {/* tabId は利用者が意識しない内部IDだが、S3 キーやジョブの記録を追う運用で必要になる。 */}
          <p className="text-xs text-muted-foreground">タブID: {tabId}</p>
        </DialogHeader>
        <div className="space-y-2">
          {query.isLoading && <Skeleton aria-label="読み込み中" className="h-24 w-full" />}
          {!query.isLoading && query.data === undefined && (
            <Alert variant="destructive">
              <AlertTitle>履歴を取得できませんでした</AlertTitle>
              <AlertDescription className="space-y-4">
                <p>{query.error instanceof Error ? query.error.message : String(query.error)}</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void query.refetch()}
                >
                  再試行
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {query.data !== undefined && revisions.length === 0 && (
            <p className="text-sm text-muted-foreground">アップロード履歴がありません</p>
          )}
          {revisions.map((revision) => (
            <div
              key={revision.revision}
              className="flex items-center justify-between gap-2 rounded-md border px-3 py-2"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">r{revision.revision}</span>
                  <Badge variant={statusBadgeVariant(revision.status)}>{revision.status}</Badge>
                  {revision.revision === activeRevision && <Badge>公開中</Badge>}
                  {revision.source === 'agent' && <Badge variant="secondary">Agent 生成</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">
                  <span>{formatDateTime(revision.createdAt)}</span> ・{' '}
                  <span>{formatSize(revision.htmlBytes + revision.dataBytes)}</span>
                </p>
              </div>
              {revision.status === 'ready' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  aria-label={`r${revision.revision}をプレビュー`}
                  onClick={() => onPreview(revision.revision)}
                >
                  プレビュー
                </Button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
